import { TEmployeeRole, TMachineDetailsWithAuxInfo, TMachineListingTab, TRESPONSE_MACHINE_LIST } from '@monorepo/types';
import { ModifiersZTableModel } from '../../table.modifier';
import { TTableTextCellColors, TTableTreeModel } from '../../table.type';
import _ from 'lodash';

const getMachineStatusDisplayInfo = (
  status: TRESPONSE_MACHINE_LIST['machines'][number]['machine']['machineStatus'],
): {
  displayTxt: string;
  textColor: TTableTextCellColors;
} => {
  switch (status) {
    case 'ACTIVE':
      return {
        displayTxt: 'table_model.Active',
        textColor: 'GREEN',
      };
    case 'AVAILABLE':
      return {
        displayTxt: 'table_model.Available',
        textColor: 'DEFAULT',
      };
    case 'NOT_USABLE':
      return {
        displayTxt: 'table_model.NotUsable',
        textColor: 'RED',
      };
    case 'NOT_AVAILABLE':
      return {
        displayTxt: 'table_model.NotAvailable',
        textColor: 'RED',
      };
    case 'IN_MAINTENANCE':
      return {
        displayTxt: 'table_model.UnderRepair',
        textColor: 'RED',
      };
    case 'PENDING_APPROVAL':
      return {
        displayTxt: 'table_model.PendingApproval',
        textColor: 'YELLOW',
      };
    case 'DELETED': {
      return {
        displayTxt: '--',
        textColor: 'DEFAULT',
      };
    }
    case 'RETURNED_TO_VENDOR':
      return {
        displayTxt: 'table_model.ReturnedToVendor',
        textColor: 'DEFAULT',
      };
  }
};

export const MACHINE_LIST_TABLE_MODEL = {
  createModel: (
    items: TRESPONSE_MACHINE_LIST['machines'],
    isMachineEditAllowed: boolean,
    isSelectedFactoryDefaultFactory: boolean,
    employeeRole?: TEmployeeRole[number] | undefined,
    selectedMachines?: TRESPONSE_MACHINE_LIST['machines'],
    listingTab: TMachineListingTab = 'WITH_FACTORY',
  ) => {
    const isWithFactoryTab = listingTab === 'WITH_FACTORY';
    return ModifiersZTableModel.createTableModel(
      items,
      {
        padding: 8,
        select: isSelectedFactoryDefaultFactory ? { position: 'START', isMulti: true, size: 'DEFAULT' } : undefined,
        headerConfig: _.compact([
          employeeRole === 'FACTORY_GROUP_ADMIN'
            ? {
                type: 'TEXT_CELL',
                text: 'FACTORY ID',
              }
            : undefined,
          {
            type: 'TEXT_CELL',
            text: 'table_model.MACHINETYPE',
          },
          {
            type: 'TEXT_CELL',
            text: 'table_model.CATEGORY',
          },
          {
            type: 'TEXT_CELL',
            text: 'table_model.SUB-CATEGORY',
          },
          {
            type: 'TEXT_CELL',
            text: 'table_model.BRAND',
          },
          isWithFactoryTab && {
            type: 'TEXT_CELL',
            text: 'table_model.MACHINELOCATION',
          },
          {
            type: 'TEXT_CELL',
            text: 'table_model.MACHINESERIALID',
          },
          {
            type: 'TEXT_CELL',
            text: 'table_model.MACHINEMODELNO',
          },
          isWithFactoryTab && {
            type: 'TEXT_CELL',
            text: 'table_model.TransferredFrom',
          },
          {
            type: 'TEXT_CELL',
            text: 'table_model.TYPEOFPURCHASE',
          },
          isWithFactoryTab && {
            type: 'TEXT_CELL',
            text: 'table_model.MACHINESTATUS',
          },
          {
            type: 'TEXT_CELL',
            text: '',
            widthInPx: 32,
          },
          isMachineEditAllowed &&
            isSelectedFactoryDefaultFactory &&
            isWithFactoryTab && {
              type: 'TEXT_CELL',
              text: '',
              widthInPx: 32,
            },
          {
            type: 'TEXT_CELL',
            text: '',
            widthInPx: 32,
          },
        ]),
      },
      (item) => {
        const machineDisplayInfo = getMachineStatusDisplayInfo(item.machine.machineStatus);
        return {
          type: 'ROW',
          rowSelectionInfo: {
            isSelected: !!selectedMachines?.find(
              (machine) => machine.machine.uniqueMachineId === item.machine.uniqueMachineId,
            ),
            isDisabled: false,
          },
          rowItem: item,
          cells: _.compact([
            employeeRole === 'FACTORY_GROUP_ADMIN'
              ? {
                  type: 'TEXT_CELL',
                  text: item.machine.factoryId,
                }
              : undefined,
            {
              type: 'TEXT_CELL',
              text: item.machine.machineType,
            },
            {
              type: 'TEXT_CELL',
              text: item.machine.machineCategory,
            },
            {
              type: 'TEXT_CELL',
              text: item.machine.machineSubCategory || '--',
            },
            {
              type: 'TEXT_CELL',
              text: item.machine.brand,
            },
            isWithFactoryTab && {
              type: 'TEXT_CELL',
              text: item.locationDetails ? item.locationDetails.locationDisplayCode : 'table_model.NotAssigned',
            },
            {
              type: 'TEXT_CELL',
              text: item.machine.factorySerialNumber,
            },
            {
              type: 'TEXT_CELL',
              text: item.machine.machineModelNumber || '--',
            },
            isWithFactoryTab && {
              type: 'TEXT_CELL',
              text: item.machine.previousFactoryIdOpt || '--',
            },
            {
              type: 'TEXT_CELL',
              text: item.machine.ownershipType,
            },
            isWithFactoryTab && {
              type: 'TEXT_CELL',
              text: machineDisplayInfo.displayTxt,
              textColor: machineDisplayInfo.textColor,
            },
            {
              type: 'ACTION_CELL',
              actionType: 'VIEW',
              asIcon: true,
            },
            isMachineEditAllowed &&
              isSelectedFactoryDefaultFactory &&
              isWithFactoryTab && {
                type: 'ACTION_CELL',
                actionType: 'EDIT',
                asIcon: true,
              },
            isSelectedFactoryDefaultFactory &&
            (item.machine.machineStatus === 'AVAILABLE' || item.machine.machineStatus === 'NOT_AVAILABLE') &&
            !!item.locationDetails
              ? {
                  type: 'ACTION_CELL',
                  actionType: 'TRANSFER',
                  asIcon: true,
                }
              : {
                  type: 'TEXT_CELL',
                  text: '',
                },
          ]),
        };
      },
    );
  },
  deselectAllRows: (
    tableModel: TTableTreeModel<TMachineDetailsWithAuxInfo>,
  ): TTableTreeModel<TMachineDetailsWithAuxInfo> => {
    return ModifiersZTableModel.deselectAllRows(tableModel);
  },
};
